/**
 * Slugify compatible con github-slugger (lo que usa fumadocs internamente
 * para generar `id` en headings). Cubre los casos comunes: minúsculas, sin
 * tildes, espacios → guiones, sin puntuación.
 */
export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // tildes
    .replace(/[^\w\s-]/g, "") // puntuación
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Resalta visualmente un heading durante ~1.6s. Usa outline para no afectar
 * layout. Si el elemento ya tenía outline, lo restaura.
 */
export function highlightSection(el: HTMLElement, durationMs = 1600) {
  const prevOutline = el.style.outline;
  const prevOffset = el.style.outlineOffset;
  const prevTransition = el.style.transition;
  const prevRadius = el.style.borderRadius;

  el.style.transition = "outline 0.3s ease, outline-offset 0.3s ease";
  el.style.outline = "3px solid var(--primary, #6366f1)";
  el.style.outlineOffset = "6px";
  el.style.borderRadius = "4px";

  setTimeout(() => {
    el.style.outline = prevOutline;
    el.style.outlineOffset = prevOffset;
    el.style.borderRadius = prevRadius;
    setTimeout(() => {
      el.style.transition = prevTransition;
    }, 360);
  }, durationMs);
}

/**
 * Busca el heading por id en la página actual; si no lo encuentra por id,
 * intenta por textContent (fallback robusto cuando el slug del LLM no
 * coincide 1:1 con github-slugger).
 */
export function findHeadingElement(
  slug: string,
  fallbackText?: string,
): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const byId = document.getElementById(slug) as HTMLElement | null;
  if (byId) return byId;
  if (!fallbackText) return null;
  const normalized = fallbackText.trim().toLowerCase();
  const all = document.querySelectorAll<HTMLElement>(
    "h1, h2, h3, h4, h5, h6",
  );
  for (const h of Array.from(all)) {
    if (h.textContent?.trim().toLowerCase() === normalized) return h;
  }
  return null;
}
